import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { softDeleteUsuario, type UsuarioItem } from '@/shared/api/controllers/usuarios';
import { USUARIOS_QUERY_KEY } from './queries';

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  usuario: UsuarioItem | null;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
};

export function DeleteUsuarioDialog({
  open,
  onOpenChange,
  usuario,
  onSuccess,
  onError,
}: Props) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: async (id: string) => softDeleteUsuario(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: USUARIOS_QUERY_KEY });
      onOpenChange(false);
      onSuccess('Usuario eliminado correctamente.');
    },
    onError: (err) => {
      const message = err instanceof Error ? err.message : 'No se pudo eliminar el usuario.';
      onOpenChange(false);
      onError(message);
    },
  });

  if (!usuario) return null;
  const nombre = usuario.fields.Nombre_USR || usuario.fields.UsuarioApp_USR;

  return (
    <Dialog open={open} onOpenChange={(o) => !mutation.isPending && onOpenChange(o)}>
      <DialogContent
        className="sm:max-w-md"
        processing={mutation.isPending && 'Eliminando usuario…'}
      >
        <DialogHeader>
          <DialogTitle>Eliminar usuario</DialogTitle>
          <DialogDescription>
            ¿Confirmás la eliminación de{' '}
            <strong className="text-foreground">{nombre || '—'}</strong>?
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={() => mutation.mutate(usuario.id)}
            disabled={mutation.isPending}
          >
            Eliminar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
